import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, CircularProgress, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Skeleton, List, ListItemAvatar, Avatar, ListItemText, IconButton, Badge,
  ListItemButton, Drawer, useTheme, useMediaQuery, Fade, Slide, Card, CardContent,
  InputAdornment, Chip, Paper, Divider, Tooltip, Zoom, alpha, styled
} from '@mui/material';
import {
  Search, Menu, ChevronLeft, Edit, PersonAdd, ChatBubbleOutline, ChevronRight, AddComment,
  ArrowBack, Close, Send, MoreVert, FilterList, Refresh
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import API from '../../API';
import ChatScreen from '../../Components/ChatScreen';
import useIsDesktop from '../../Hooks/useIsDesktop';
import useAuth from '../../Hooks/use-auth';
import { useCreatorAuth } from '../../Hooks/creator-use-auth';
import useAlertDialog from '../../Components/useAlertDialog';
import AlertDialog from '../../Components/AlertDialog';
import CreateNewChatDialog from '../../Components/ChatComponents/newchat';
import ParticipantsPanel from '../../Components/ChatComponents/chatextra';
import UpdateConversationDetailsDialog from '../../Components/ChatComponents/updatedetails';
import ChatModalCreator from '../../Components/ChatComponents/creatorchatmodal';

/* ------------------------------------------------------------------ */
/* Styled Components & Constants */
/* ------------------------------------------------------------------ */
const BOTTOM_NAV_HEIGHT = 86;
const HEADER_HEIGHT = 64;

// FIXED: Updated z-index values to ensure proper stacking
const Z_INDEX = {
  MOBILE_CHAT_OVERLAY: 1000,
  DIALOG_BACKDROP: 1300,
  DIALOG: 1400,
  MOBILE_DIALOG: 1500, // Higher for mobile when chat is open
};

// Modern styled components
const StyledSearchField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 28,
    backgroundColor: alpha(theme.palette.background.paper, 0.8),
    transition: theme.transitions.create(['background-color', 'box-shadow']),
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.background.paper,
      boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    },
  },
}));

const ConversationCard = styled(Paper)(({ theme, selected }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: 16,
  transition: theme.transitions.create(['transform', 'box-shadow', 'background-color'], {
    duration: 200,
  }),
  cursor: 'pointer',
  border: selected ? `2px solid ${theme.palette.primary.main}` : '2px solid transparent',
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.08)
    : theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: theme.shadows[4],
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.action.hover, 0.04),
  },
  '&:active': {
    transform: 'translateY(0px)',
  },
}));

const HeaderContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  backdropFilter: 'blur(10px)',
  position: 'sticky',
  top: 0,
  zIndex: 10,
  flexShrink: 0,
}));

const EmptyStateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
}));

/* ------------------------------------------------------------------ */
/* Fixed ConversationsHub Component */
/* ------------------------------------------------------------------ */
export default function ConversationsHub() {
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = !useIsDesktop();
  const location = useLocation();
  const navigate = useNavigate();
  const { dialogState, openDialog, closeDialog } = useAlertDialog();

  // Enhanced device detection
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isMobileSafari = isMobile && isSafari;

  /* ---------------- Auth & User Type Detection ---------------- */
  const { getCurrrentUser } = useAuth();
  const userInfo = getCurrrentUser();
  const { creatorToken } = useCreatorAuth();
  const userId = userInfo?.id || creatorToken?.creator_user?.username || null;
  const isCreator = !!creatorToken;
  const isUser = !!userInfo && !creatorToken;
  // FIXED: Both users and creators should have access to participant management
  const hasParticipantAccess = isCreator || isUser;

  /* ---------------- Enhanced State Management ---------------- */
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [addParticipantOpen, setAddParticipantOpen] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [newChatOpen, setNewChatOpen] = useState(false);
  const [updateDetailsDialogOpen, setUpdateDetailsDialogOpen] = useState(false);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Creator-specific state
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [newChatUserId, setNewChatUserId] = useState(null);

  /* ---------------- Dynamic API Configuration ---------------- */
  const apiConfig = useMemo(() => {
    if (isCreator) {
      return {
        queryKey: 'creatorConversations',
        fetchFn: () => API.conversations.fetchCreatorConversations(),
        createFn: (params) => API.conversations.checkCreatorToUser(params),
      };
    } else {
      return {
        queryKey: ['messages', 'userSentConversations'],
        fetchFn: () => API.conversations.fetchUserToUser(),
        createFn: (params) => API.conversations.checkUserToUser(params),
      };
    }
  }, [isCreator]);

  // FIXED: Improved z-index calculation for proper dialog stacking
  const getDialogZIndex = useCallback(() => {
    if (isMobile && selectedConversationId) {
      return Z_INDEX.MOBILE_DIALOG;
    }
    return Z_INDEX.DIALOG;
  }, [isMobile, selectedConversationId]);

  /* ---------------- Enhanced Queries ---------------- */
  const {
    data: conversations = [],
    isLoading: isLoadingConversations,
    error: convError,
    refetch: refetchConversations,
    isFetching: isFetchingConversations
  } = useQuery(
    apiConfig.queryKey,
    apiConfig.fetchFn,
    {
      initialData: [],
      staleTime: 1000 * 60 * 5, // 5 minutes
      cacheTime: 1000 * 60 * 30, // 30 minutes
      enabled: !!(isCreator || isUser),
      refetchOnMount: true,
      refetchOnWindowFocus: true,
    }
  );

  const {
    data: usersList = [],
    isLoading: isLoadingUsers
  } = useQuery(
    ['users'],
    () => API.users.publicList(),
    {
      refetchOnWindowFocus: false,
      initialData: [],
      staleTime: 1000 * 60 * 10,
    }
  );

  /* ---------------- Enhanced Mutations ---------------- */
  const createNewChatMutation = useMutation(
    (params) => apiConfig.createFn(params),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        handleCloseNewChat();
        handleSelectConversation(res.conversation_id);
      },
      onError: (err) => {
        console.error('Error creating new conversation:', err);
        openDialog('Error', `Error creating conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const inviteByEmailMutation = useMutation(
    (emailsArray) => API.conversations.inviteByEmail(selectedConversationId, emailsArray),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        queryClient.invalidateQueries(['messages', selectedConversationId]);
        handleCloseAddParticipant();
      },
      onError: (err) => {
        console.error('Error inviting by email:', err);
        openDialog('Error', `Error inviting to conversation: ${err?.response?.data?.error || err.message}`);
      }
    }
  );

  const updateConversationDetailsMutation = useMutation(
    (details) => API.conversations.updateConversationDetails(selectedConversationId, details),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(apiConfig.queryKey);
        if (selectedConversationId) {
          queryClient.invalidateQueries(['messages', selectedConversationId]);
        }
        setUpdateDetailsDialogOpen(false);
      },
      onError: (err) => {
        console.error('Error updating conversation details:', err);
      }
    }
  );

  /* ---------------- Enhanced Helper Functions ---------------- */
  const getConvoName = useCallback((convo) => {
    if (convo.display_name) return convo.display_name;
    if (convo.campaign_name) return convo.campaign_name;
    if (convo.partnership_name) return convo.partnership_name;
    const otherUser = convo.participants?.find((p) => p.id !== userId);
    return otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : `Conversation #${convo.id}`;
  }, [userId]);

  const getConvoAvatar = useCallback((convo) => {
    // First check if there's a cover image
    if (convo.cover_image && convo.cover_image.trim()) {
      return convo.cover_image;
    }
    
    // Fall back to existing logic
    const otherUser = convo.participants?.find((p) => p.id !== userId);
    return otherUser?.phphref || '';
  }, [userId]);

  const getLastMessagePreview = useCallback((convo) => {
    if (convo.message) {
      return convo.message.length > 50
        ? `${convo.message.substring(0, 50)}...`
        : convo.message;
    }
    return 'No messages yet';
  }, []);

  const filteredConversations = useMemo(() => {
    if (!searchQuery.trim()) return conversations;
    const lowerQuery = searchQuery.toLowerCase();
    return conversations.filter((convo) =>
      getConvoName(convo).toLowerCase().includes(lowerQuery) ||
      getLastMessagePreview(convo).toLowerCase().includes(lowerQuery)
    );
  }, [conversations, searchQuery, getConvoName, getLastMessagePreview]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedConversationId),
    [conversations, selectedConversationId]
  );

  /* ---------------- Enhanced UI Handlers ---------------- */
  const toggleLeftPanel = useCallback(() => {
    setIsLeftPanelCollapsed((prev) => !prev);
  }, []);

  const handleSelectConversation = useCallback((convoId) => {
    setSelectedConversationId(convoId);
    navigate(`?conversation=${convoId}`, { replace: true });
    if (isMobile) setIsLeftPanelCollapsed(true);
  }, [navigate, isMobile]);

  const handleBackToList = useCallback(() => {
    setSelectedConversationId(null);
    navigate('', { replace: true });
    if (isLeftPanelCollapsed) setIsLeftPanelCollapsed(false);
    refetchConversations();
  }, [navigate, isLeftPanelCollapsed, refetchConversations]);

  const handleRefresh = useCallback(() => {
    refetchConversations();
  }, [refetchConversations]);

  const handleOpenNewChat = useCallback(() => setNewChatOpen(true), []);
  const handleCloseNewChat = useCallback(() => setNewChatOpen(false), []);
  const handleCloseAddParticipant = useCallback(() => {
    setAddParticipantOpen(false);
    setInviteEmails(''); // Clear emails when closing
  }, []);
  const handleToggleParticipants = useCallback(() => setParticipantsOpen((prev) => !prev), []);

  /* ---------------- iOS Safari Viewport Fix ---------------- */
  useEffect(() => {
    if (isMobileSafari) {
      const setVH = () => {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      };
      
      setVH();
      window.addEventListener('resize', setVH);
      window.addEventListener('orientationchange', setVH);
      
      return () => {
        window.removeEventListener('resize', setVH);
        window.removeEventListener('orientationchange', setVH);
      };
    }
  }, [isMobileSafari]);

  /* ---------------- URL Sync & Lifecycle Effects ---------------- */
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const convoId = searchParams.get('conversation');
    if (convoId) {
      setSelectedConversationId(Number(convoId));
      refetchConversations();
    }

    if (isCreator) {
      const withId = searchParams.get('with');
      if (withId) {
        setNewChatUserId(Number(withId));
        setChatModalOpen(true);
      }
    }
  }, [location.search, refetchConversations, isCreator]);

  useEffect(() => {
    if ((isCreator || isUser) && conversations.length === 0 && !isLoadingConversations) {
      refetchConversations();
    }
  }, [isCreator, isUser, conversations.length, isLoadingConversations, refetchConversations]);

  // FIXED: Improved body scroll management with better portal support
  useEffect(() => {
    if (isMobile && selectedConversationId) {
      // Store original values
      const originalOverflow = document.body.style.overflow;
      const originalPosition = document.body.style.position;
      const originalWidth = document.body.style.width;
      const originalHeight = document.body.style.height;
      const originalTop = document.body.style.top;

      // Prevent body scroll but maintain portal functionality
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.left = '0';
      document.body.style.right = '0';

      return () => {
        // Restore original values
        document.body.style.overflow = originalOverflow;
        document.body.style.position = originalPosition;
        document.body.style.width = originalWidth;
        document.body.style.height = originalHeight;
        document.body.style.top = originalTop;
        document.body.style.left = '';
        document.body.style.right = '';
        
        // Restore scroll position
        if (scrollY) {
          window.scrollTo(0, scrollY);
        }
      };
    }
  }, [isMobile, selectedConversationId]);

  /* ---------------- Loading & Error States ---------------- */
  if ((isCreator || isUser) && isLoadingConversations && conversations.length === 0) {
    return (
      <EmptyStateContainer>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Loading your conversations...
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Please wait while we fetch your chat history
        </Typography>
      </EmptyStateContainer>
    );
  }

  if (convError) {
    return (
      <EmptyStateContainer>
        <Typography variant="h6" color="error" gutterBottom>
          Oops! Something went wrong
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          {convError.message}
        </Typography>
        <Button variant="contained" onClick={refetchConversations} startIcon={<Refresh />}>
          Try Again
        </Button>
      </EmptyStateContainer>
    );
  }

  /* ------------------------------------------------------------------ */
  /* MOBILE VIEW - Render different components for mobile vs desktop */
  /* ------------------------------------------------------------------ */
  
  // MOBILE: Show only conversation list when no conversation selected
  if (isMobile && !selectedConversationId) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            height: `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            overflow: 'hidden',
          }}
        >
          {/* Mobile Header */}
          <HeaderContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="600">
                Conversations
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    disabled={isFetchingConversations}
                  >
                    <Refresh sx={{
                      animation: isFetchingConversations ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      }
                    }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Search Bar */}
            <StyledSearchField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{
                      color: isSearchFocused ? 'primary.main' : 'action.active',
                      transition: 'color 0.2s'
                    }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      sx={{ p: 0.5 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* New Chat Button */}
            <Button
              variant="contained"
              onClick={handleOpenNewChat}
              fullWidth
              size="large"
              startIcon={isCreator ? <AddComment /> : <Send />}
              sx={{
                mt: 2,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s',
              }}
            >
              {isCreator ? 'Start New Chat' : 'Create New Chat'}
            </Button>
          </HeaderContainer>

          {/* Mobile Conversations List */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1,
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: 3,
              },
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
            }}
          >
            {isLoadingConversations ? (
              <Box sx={{ p: 1 }}>
                {[...Array(8)].map((_, i) => (
                  <Card key={i} sx={{ mb: 1, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="circular" width={48} height={48} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} />
                        <Skeleton variant="text" width="90%" height={20} />
                      </Box>
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : filteredConversations.length === 0 ? (
              <EmptyStateContainer sx={{ height: 'auto', py: 8 }}>
                <ChatBubbleOutline sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No conversations found' : 'No conversations yet'}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start a new conversation to get chatting!'
                  }
                </Typography>
                {!searchQuery && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenNewChat}
                    sx={{ mt: 2 }}
                    startIcon={<AddComment />}
                  >
                    Start Your First Chat
                  </Button>
                )}
              </EmptyStateContainer>
            ) : (
              <List sx={{ p: 0, width: '100%' }}>
                {filteredConversations.map((convo, index) => (
                  <Zoom in timeout={200 + index * 50} key={convo.id}>
                    <div>
                      <ConversationCard
                        selected={false}
                        onClick={() => handleSelectConversation(convo.id)}
                        elevation={1}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={getConvoAvatar(convo)}
                              sx={{
                                width: 48,
                                height: 48,
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                              }}
                            >
                              {getConvoName(convo).charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="600"
                                  noWrap
                                  sx={{ color: 'text.primary' }}
                                >
                                  {getConvoName(convo)}
                                </Typography>
                                <Badge
                                  badgeContent={convo.unread_count}
                                  color="primary"
                                  max={99}
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      minWidth: 20,
                                      height: 20
                                    }
                                  }}
                                >
                                  <ChatBubbleOutline
                                    fontSize="small"
                                    sx={{
                                      color: convo.unread_count > 0 ? 'primary.main' : 'action.active',
                                      transition: 'color 0.2s'
                                    }}
                                  />
                                </Badge>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ mt: 0.5 }}
                              >
                                {getLastMessagePreview(convo)}
                              </Typography>
                              {convo.unread_count > 0 && (
                                <Chip
                                  label={`${convo.unread_count} new`}
                                  size="small"
                                  color="primary"
                                  sx={{
                                    mt: 1,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </ConversationCard>
                    </div>
                  </Zoom>
                ))}
              </List>
            )}
          </Box>

          {/* FIXED: Add dialogs to mobile conversation list view */}
          <CreateNewChatDialog
            open={newChatOpen}
            onClose={() => setNewChatOpen(false)}
            sx={{ zIndex: getDialogZIndex() }}
            onCreated={(convoId) => {
              queryClient.invalidateQueries(apiConfig.queryKey);
              setNewChatOpen(false);
              handleSelectConversation(convoId);
            }}
          />

          {isCreator && (
            <ChatModalCreator
              open={chatModalOpen}
              onClose={() => setChatModalOpen(false)}
              recipientUserId={newChatUserId}
              sx={{ zIndex: getDialogZIndex() }}
            />
          )}

          <AlertDialog alertState={dialogState} />
        </Box>
      </Fade>
    );
  }

  // MOBILE: Show only conversation view when conversation selected
  if (isMobile && selectedConversationId) {
    return (
      <Fade in timeout={300}>
        <Box
          sx={{
            height: `calc(100vh - ${BOTTOM_NAV_HEIGHT}px)`,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            position: 'relative', // Changed from fixed to relative
            // Removed fixed positioning properties that were causing overlay
            zIndex: Z_INDEX.MOBILE_CHAT_OVERLAY,
            // FIXED: Simplified iOS handling without fixed positioning
            ...(isIOS && {
              paddingLeft: 'env(safe-area-inset-left)',
              paddingRight: 'env(safe-area-inset-right)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }),
          }}
        >
          {/* Mobile Chat Header */}
          <HeaderContainer sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: HEADER_HEIGHT,
            maxHeight: HEADER_HEIGHT,
            py: 1.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Tooltip title="Back to conversations">
                <IconButton onClick={handleBackToList} sx={{ mr: 1 }}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              {selectedConversation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={getConvoAvatar(selectedConversation)}
                    sx={{ width: 40, height: 40 }}
                  >
                    {getConvoName(selectedConversation).charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight="600" noWrap>
                      {selectedConversation.display_name || getConvoName(selectedConversation)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedConversation.participants?.length || 0} participants
                    </Typography>
                  </Box>
                </Box>
              )}
              {/* FIXED: Both creators and users can edit conversation details */}
              {selectedConversationId && hasParticipantAccess && (
                <Tooltip title="Edit conversation details">
                  <IconButton onClick={() => setUpdateDetailsDialogOpen(true)} size="small">
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Manage participants">
                <IconButton onClick={handleToggleParticipants}>
                  <PersonAdd />
                </IconButton>
              </Tooltip>
              <Tooltip title="More options">
                <IconButton>
                  <MoreVert />
                </IconButton>
              </Tooltip>
            </Box>
          </HeaderContainer>

          {/* Mobile Chat Content */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            height: `calc(100% - ${HEADER_HEIGHT}px)`,
          }}>
            <Fade in timeout={300}>
              <Box sx={{ 
                flex: 1, 
                display: 'flex', 
                flexDirection: 'column',
                minHeight: 0,
                position: 'relative',
                height: '100%',
              }}>
                <ChatScreen
                  conversationId={selectedConversationId}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'visible',
                    position: 'relative',
                    WebkitOverflowScrolling: 'touch',
                  }}
                />
              </Box>
            </Fade>
          </Box>

          {/* FIXED: Add all dialogs to mobile chat view with proper z-index - Now available to both users and creators */}
          <CreateNewChatDialog
            open={newChatOpen}
            onClose={() => setNewChatOpen(false)}
            sx={{ zIndex: getDialogZIndex() }}
            onCreated={(convoId) => {
              queryClient.invalidateQueries(apiConfig.queryKey);
              setNewChatOpen(false);
              handleSelectConversation(convoId);
            }}
          />

          {/* FIXED: Participant management available to both users and creators */}
          {hasParticipantAccess && (
            <>
              <Dialog
                open={addParticipantOpen}
                onClose={handleCloseAddParticipant}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: getDialogZIndex() }}
                PaperProps={{
                  sx: {
                    borderRadius: { xs: 0, sm: 3 },
                    height: { xs: '100vh', sm: '80vh' },
                    maxHeight: { xs: '100vh', sm: '80vh' },
                  }
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="h6" fontWeight="600">
                      Add Participant
                    </Typography>
                    {isMobile && (
                      <IconButton onClick={handleCloseAddParticipant}>
                        <Close />
                      </IconButton>
                    )}
                  </Box>
                </DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
                  {isLoadingUsers ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                        Invite by email (comma-separated for multiple):
                      </Typography>
                      <TextField
                        label="Email addresses"
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={3}
                        value={inviteEmails}
                        onChange={(e) => setInviteEmails(e.target.value)}
                        placeholder="john@example.com, jane@example.com"
                        sx={{ mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        onClick={() => {
                          const trimmed = inviteEmails.trim();
                          if (!trimmed) return;
                          const emailsArray = trimmed.split(',').map((e) => e.trim()).filter(Boolean);
                          inviteByEmailMutation.mutate({ emails: emailsArray });
                        }}
                        disabled={inviteByEmailMutation.isLoading || !inviteEmails.trim()}
                        fullWidth
                        sx={{ borderRadius: 2 }}
                      >
                        {inviteByEmailMutation.isLoading ? 'Sending Invites...' : 'Send Invitations'}
                      </Button>
                    </Box>
                  )}
                </DialogContent>
                {!isMobile && (
                  <DialogActions sx={{ px: 3, pb: 3 }}>
                    <Button onClick={handleCloseAddParticipant} sx={{ borderRadius: 2 }}>
                      Cancel
                    </Button>
                  </DialogActions>
                )}
              </Dialog>

              <Dialog
                open={participantsOpen}
                onClose={handleToggleParticipants}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                sx={{ zIndex: getDialogZIndex() }}
                PaperProps={{
                  sx: {
                    borderRadius: { xs: 0, sm: 3 },
                    height: { xs: '85vh', sm: '80vh' },
                    maxHeight: { xs: '85vh', sm: '80vh' },
                  }
                }}
              >
                <ParticipantsPanel
                  conversationId={selectedConversationId}
                  onClose={handleToggleParticipants}
                />
              </Dialog>

              <UpdateConversationDetailsDialog
                open={updateDetailsDialogOpen}
                onClose={() => setUpdateDetailsDialogOpen(false)}
                conversation={selectedConversation}
                sx={{ zIndex: getDialogZIndex() }}
              />
            </>
          )}

          {isCreator && (
            <ChatModalCreator
              open={chatModalOpen}
              onClose={() => setChatModalOpen(false)}
              recipientUserId={newChatUserId}
              sx={{ zIndex: getDialogZIndex() }}
            />
          )}

          <AlertDialog alertState={dialogState} />
        </Box>
      </Fade>
    );
  }

  /* ------------------------------------------------------------------ */
  /* DESKTOP VIEW - Original layout for desktop */
  /* ------------------------------------------------------------------ */
  return (
    <Fade in timeout={300}>
      <Box
        sx={{
          display: 'flex',
          height: `calc(100vh - 1px)`,
          width: '100%',
          overflow: 'hidden',
          bgcolor: 'background.default',
          position: 'relative',
        }}
      >
        {/* Desktop Left Panel - Conversations List */}
        <Box
          sx={{
            width: isLeftPanelCollapsed ? 0 : 400,
            minWidth: isLeftPanelCollapsed ? 0 : 400,
            height: '100%',
            transition: theme.transitions.create(['width', 'min-width'], {
              duration: 300,
              easing: theme.transitions.easing.easeInOut,
            }),
            overflow: 'hidden',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Header */}
          <HeaderContainer>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h5" fontWeight="600">
                Conversations
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh">
                  <IconButton
                    onClick={handleRefresh}
                    size="small"
                    disabled={isFetchingConversations}
                  >
                    <Refresh sx={{
                      animation: isFetchingConversations ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' },
                      }
                    }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isLeftPanelCollapsed ? "Expand" : "Collapse"}>
                  <IconButton onClick={toggleLeftPanel} size="small">
                    <ChevronRight sx={{
                      transform: isLeftPanelCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                      transition: 'transform 0.2s'
                    }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Search Bar */}
            <StyledSearchField
              fullWidth
              size="small"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={() => setIsSearchFocused(false)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{
                      color: isSearchFocused ? 'primary.main' : 'action.active',
                      transition: 'color 0.2s'
                    }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchQuery('')}
                      sx={{ p: 0.5 }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* New Chat Button */}
            <Button
              variant="contained"
              onClick={handleOpenNewChat}
              fullWidth
              size="large"
              startIcon={isCreator ? <AddComment /> : <Send />}
              sx={{
                mt: 2,
                borderRadius: 3,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                boxShadow: 2,
                '&:hover': {
                  boxShadow: 4,
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s',
              }}
            >
              {isCreator ? 'Start New Chat' : 'Create New Chat'}
            </Button>
          </HeaderContainer>

          {/* Conversations List */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              p: 1,
              '&::-webkit-scrollbar': {
                width: 6,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: alpha(theme.palette.text.primary, 0.2),
                borderRadius: 3,
              },
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth',
            }}
          >
            {isLoadingConversations ? (
              <Box sx={{ p: 1 }}>
                {[...Array(8)].map((_, i) => (
                  <Card key={i} sx={{ mb: 1, p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Skeleton variant="circular" width={48} height={48} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton variant="text" width="70%" height={24} />
                        <Skeleton variant="text" width="90%" height={20} />
                      </Box>
                      <Skeleton variant="circular" width={24} height={24} />
                    </Box>
                  </Card>
                ))}
              </Box>
            ) : filteredConversations.length === 0 ? (
              <EmptyStateContainer sx={{ height: 'auto', py: 8 }}>
                <ChatBubbleOutline sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {searchQuery ? 'No conversations found' : 'No conversations yet'}
                </Typography>
                <Typography variant="body2" color="text.disabled">
                  {searchQuery
                    ? 'Try adjusting your search terms'
                    : 'Start a new conversation to get chatting!'
                  }
                </Typography>
                {!searchQuery && (
                  <Button
                    variant="outlined"
                    onClick={handleOpenNewChat}
                    sx={{ mt: 2 }}
                    startIcon={<AddComment />}
                  >
                    Start Your First Chat
                  </Button>
                )}
              </EmptyStateContainer>
            ) : (
              <List sx={{ p: 0, width: '100%' }}>
                {filteredConversations.map((convo, index) => (
                  <Zoom in timeout={200 + index * 50} key={convo.id}>
                    <div>
                      <ConversationCard
                        selected={convo.id === selectedConversationId}
                        onClick={() => handleSelectConversation(convo.id)}
                        elevation={convo.id === selectedConversationId ? 3 : 1}
                      >
                        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={getConvoAvatar(convo)}
                              sx={{
                                width: 48,
                                height: 48,
                                border: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`
                              }}
                            >
                              {getConvoName(convo).charAt(0).toUpperCase()}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography
                                  variant="subtitle1"
                                  fontWeight="600"
                                  noWrap
                                  sx={{
                                    color: convo.id === selectedConversationId ? 'primary.main' : 'text.primary',
                                    transition: 'color 0.2s'
                                  }}
                                >
                                  {getConvoName(convo)}
                                </Typography>
                                <Badge
                                  badgeContent={convo.unread_count}
                                  color="primary"
                                  max={99}
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.75rem',
                                      fontWeight: 600,
                                      minWidth: 20,
                                      height: 20
                                    }
                                  }}
                                >
                                  <ChatBubbleOutline
                                    fontSize="small"
                                    sx={{
                                      color: convo.unread_count > 0 ? 'primary.main' : 'action.active',
                                      transition: 'color 0.2s'
                                    }}
                                  />
                                </Badge>
                              </Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                noWrap
                                sx={{ mt: 0.5 }}
                              >
                                {getLastMessagePreview(convo)}
                              </Typography>
                              {convo.unread_count > 0 && (
                                <Chip
                                  label={`${convo.unread_count} new`}
                                  size="small"
                                  color="primary"
                                  sx={{
                                    mt: 1,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    fontWeight: 600
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </CardContent>
                      </ConversationCard>
                    </div>
                  </Zoom>
                ))}
              </List>
            )}
          </Box>
        </Box>

        {/* Desktop Right Panel - Chat View */}
        <Box
          sx={{
            flex: 1,
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.default',
            borderLeft: selectedConversationId ? '1px solid' : 'none',
            borderColor: 'divider',
            position: 'relative',
          }}
        >
          {/* Desktop Chat Header */}
          <HeaderContainer sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            minHeight: HEADER_HEIGHT,
            maxHeight: HEADER_HEIGHT,
            py: 1,
            px: 1.5,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
              <Tooltip title={isLeftPanelCollapsed ? "Show sidebar" : "Hide sidebar"}>
                <IconButton onClick={toggleLeftPanel} sx={{ mr: 1 }}>
                  {isLeftPanelCollapsed ? <Menu /> : <ChevronLeft />}
                </IconButton>
              </Tooltip>
              {selectedConversation && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1, minWidth: 0 }}>
                  <Avatar
                    src={getConvoAvatar(selectedConversation)}
                    sx={{ width: 40, height: 40 }}
                  >
                    {getConvoName(selectedConversation).charAt(0).toUpperCase()}
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" fontWeight="600" noWrap>
                      {selectedConversation.display_name || getConvoName(selectedConversation)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedConversation.participants?.length || 0} participants
                    </Typography>
                  </Box>
                </Box>
              )}
              {!selectedConversation && (
                <Typography variant="h6" color="text.secondary">
                  Select a conversation to start chatting
                </Typography>
              )}
              {/* FIXED: Both creators and users can edit conversation details */}
              {selectedConversationId && hasParticipantAccess && (
                <Tooltip title="Edit conversation details">
                  <IconButton onClick={() => setUpdateDetailsDialogOpen(true)} size="small">
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {selectedConversationId && (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Manage participants">
                  <IconButton onClick={handleToggleParticipants}>
                    <PersonAdd />
                  </IconButton>
                </Tooltip>
                <Tooltip title="More options">
                  <IconButton>
                    <MoreVert />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </HeaderContainer>

          {/* Desktop Chat Content */}
          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            height: `calc(100% - ${HEADER_HEIGHT}px)`,
            maxHeight: `calc(100% - ${HEADER_HEIGHT}px)`,
          }}>
            {selectedConversationId ? (
              <Fade in timeout={300}>
                <Box sx={{ 
                  flex: 1, 
                  display: 'flex', 
                  flexDirection: 'column',
                  minHeight: 0,
                  position: 'relative',
                  height: '100%',
                }}>
                  <ChatScreen
                    conversationId={selectedConversationId}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      overflow: 'visible',
                      position: 'relative',
                    }}
                  />
                </Box>
              </Fade>
            ) : (
              <EmptyStateContainer>
                <ChatBubbleOutline sx={{ fontSize: 80, color: 'text.disabled', mb: 3 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom fontWeight="500">
                  Welcome to Chat
                </Typography>
                <Typography variant="body1" color="text.disabled" paragraph>
                  Select a conversation from the sidebar to start chatting,
                  or create a new conversation to get started.
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleOpenNewChat}
                  startIcon={<AddComment />}
                  size="large"
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 4,
                    py: 1.5,
                  }}
                >
                  Start New Conversation
                </Button>
              </EmptyStateContainer>
            )}
          </Box>
        </Box>

        {/* Dialogs and Modals - Desktop */}
        <CreateNewChatDialog
          open={newChatOpen}
          onClose={() => setNewChatOpen(false)}
          sx={{ zIndex: getDialogZIndex() }}
          onCreated={(convoId) => {
            queryClient.invalidateQueries(apiConfig.queryKey);
            setNewChatOpen(false);
            handleSelectConversation(convoId);
          }}
        />

        {/* FIXED: Participant management available to both users and creators */}
        {hasParticipantAccess && (
          <>
            <Dialog
              open={addParticipantOpen}
              onClose={handleCloseAddParticipant}
              maxWidth="sm"
              fullWidth
              sx={{ zIndex: getDialogZIndex() }}
            >
              <DialogTitle sx={{ pb: 1 }}>
                <Typography variant="h6" fontWeight="600">
                  Add Participant
                </Typography>
              </DialogTitle>
              <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
                {isLoadingUsers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                      Invite by email (comma-separated for multiple):
                    </Typography>
                    <TextField
                      label="Email addresses"
                      variant="outlined"
                      fullWidth
                      multiline
                      rows={3}
                      value={inviteEmails}
                      onChange={(e) => setInviteEmails(e.target.value)}
                      placeholder="john@example.com, jane@example.com"
                      sx={{ mb: 2 }}
                    />
                    <Button
                      variant="contained"
                      onClick={() => {
                        const trimmed = inviteEmails.trim();
                        if (!trimmed) return;
                        const emailsArray = trimmed.split(',').map((e) => e.trim()).filter(Boolean);
                        inviteByEmailMutation.mutate({ emails: emailsArray });
                      }}
                      disabled={inviteByEmailMutation.isLoading || !inviteEmails.trim()}
                      fullWidth
                      sx={{ borderRadius: 2 }}
                    >
                      {inviteByEmailMutation.isLoading ? 'Sending Invites...' : 'Send Invitations'}
                    </Button>
                  </Box>
                )}
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleCloseAddParticipant} sx={{ borderRadius: 2 }}>
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={participantsOpen}
              onClose={handleToggleParticipants}
              maxWidth="sm"
              fullWidth
              sx={{ zIndex: getDialogZIndex() }}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  height: '80vh',
                  maxHeight: '80vh',
                }
              }}
            >
              <ParticipantsPanel
                conversationId={selectedConversationId}
                onClose={handleToggleParticipants}
              />
            </Dialog>

            <UpdateConversationDetailsDialog
              open={updateDetailsDialogOpen}
              onClose={() => setUpdateDetailsDialogOpen(false)}
              conversation={selectedConversation}
              sx={{ zIndex: getDialogZIndex() }}
            />
          </>
        )}

        {isCreator && (
          <ChatModalCreator
            open={chatModalOpen}
            onClose={() => setChatModalOpen(false)}
            recipientUserId={newChatUserId}
            sx={{ zIndex: getDialogZIndex() }}
          />
        )}

        <AlertDialog alertState={dialogState} />
      </Box>
    </Fade>
  );
}